export type BannerAbTestMode = 'pairwise' | 'auto_loop';
export type BannerAbTestStatus = 'pending' | 'preparing' | 'evaluating' | 'generating' | 'completed' | 'failed' | 'cancelled';
export type BannerAbVerdict = 'a' | 'b' | 'undecided';
export type BannerAbFactor = 'copy' | 'visual' | 'offer' | 'trust' | 'layout' | 'other';
export type BannerAbComparisonStatus = 'pending' | 'voting' | 'aggregated' | 'failed';

/**
 * ラウンドの進行状態（`banner_ab_test_rounds.status`）。
 * `running` は PR1 の pairwise round 0 が書く値で、`BannerAbTestRound::STATUS_RUNNING` に対応する。
 * 含めないと BANNER_AB_ROUND_STATUS_LABELS[round.status] が undefined を返しうる型の嘘になる。
 */
export type BannerAbRoundStatus = 'running' | 'hypothesize' | 'generating' | 'voting' | 'completed' | 'failed';

/**
 * 停止理由。語彙は BANNER_AB_STOPPED_REASON_LABELS のキーと 1:1 なので、増やすときは両方直す。
 * `generation_failed` は 2ラウンド目以降の派生案全滅（`haltForGenerationFailure()`）。
 * `max_ticks` は現状どのコードも書かない（tick 上限は `giveUp()` を通って `deadline` に畳まれる）。
 */
export type BannerAbStoppedReason =
  | 'no_improvement'
  | 'max_rounds'
  | 'budget'
  | 'generation_failed'
  | 'cancelled'
  | 'deadline'
  | 'max_ticks'
  | 'undecided'
  | 'error';

export const PERSONA_COUNT_OPTIONS = [100, 200, 300, 500] as const;
export const DEFAULT_PERSONA_COUNT = 200;
/** banner_ab_test.max_max_rounds = 5 と揃える。既定は default_max_rounds = 3 */
export const MAX_ROUNDS_OPTIONS = [1, 2, 3, 4, 5] as const;
export const DEFAULT_MAX_ROUNDS = 3;
export const BANNER_AB_DISCLAIMER =
  'LLM が演じる仮想顧客による予測です。実際の配信結果を保証するものではなく、見込みの薄い案を事前に絞り込むための参考情報です。';
/**
 * 「選ばれた割合」の読み方。票は同じ LLM が演じるので、人数を増やしても「確からしさ」は上がらない。
 * 参考にした論文（arXiv 2609.01038）では、ペルソナ集団の予測と実際の A/B テストの勝敗の一致は 75〜90%。
 */
export const BANNER_AB_PREFERENCE_NOTE =
  '「選ばれた割合」は仮想顧客のうち、その案をクリックしたいと答えた人の割合です（同点は除く）。仮想顧客の予測が実際の A/B テストの勝敗と一致するのは、研究では 7〜9 割程度です。';

/** 進行中（＝ポーリングを回す）とみなすステータス */
export const BANNER_AB_ACTIVE_STATUSES: readonly BannerAbTestStatus[] = [
  'pending', 'preparing', 'evaluating', 'generating',
];

export const BANNER_AB_MODE_LABELS: Record<BannerAbTestMode, string> = {
  pairwise: 'ペア比較',
  auto_loop: '自動改善ループ',
};

/** 一覧・結果ページで共通のステータス表示。文言と色をここ1箇所で持つ */
export const BANNER_AB_STATUS_LABELS: Record<BannerAbTestStatus, string> = {
  pending: '待機中',
  preparing: '仮想顧客を準備中',
  evaluating: '採点中',
  generating: '派生案を生成中',
  completed: '完了',
  failed: '失敗',
  cancelled: 'キャンセル',
};

/** TaskyBadge の variant 名。進行中は info、終端は成否で出し分ける */
export const BANNER_AB_STATUS_VARIANTS: Record<
  BannerAbTestStatus,
  'secondary' | 'info' | 'success' | 'destructive'
> = {
  pending: 'info',
  preparing: 'info',
  evaluating: 'info',
  generating: 'info',
  completed: 'success',
  failed: 'destructive',
  cancelled: 'secondary',
};

/**
 * stopped_reason の表示文言。ループは「止まった理由」が成果物の読み方を決めるのでここ1箇所で持つ。
 * 語彙は BannerAbTestTickJob の停止経路と揃える（undecided / error は pairwise 由来）。
 * 未知の理由は呼び出し側でフォールバックする（stopped_reason は string | null なので Record<string, string>）。
 */
export const BANNER_AB_STOPPED_REASON_LABELS: Record<string, string> = {
  no_improvement: '改善が見られなくなったため終了',
  max_rounds: '設定したラウンド数に達したため終了',
  budget: '設定した予算に達したため終了',
  generation_failed: '派生案の生成に失敗したため、確定済みの推奨で終了',
  cancelled: 'キャンセルされました',
  deadline: '時間内に完了しなかったため終了',
  max_ticks: '処理が想定より長く続いたため終了',
  undecided: '有意な差が出なかったため勝者なしで終了',
  error: 'エラーが発生したため終了',
};

/** ラウンド行のステータス表示。語彙は BannerAbTestRound の STATUS_* 定数と揃える */
export const BANNER_AB_ROUND_STATUS_LABELS: Record<BannerAbRoundStatus, string> = {
  running: '進行中',
  hypothesize: '改善案を検討中',
  generating: '派生案を生成中',
  voting: '採点中',
  completed: '完了',
  failed: '失敗',
};

export const FACTOR_LABELS: Record<BannerAbFactor, string> = {
  copy: 'コピー',
  visual: 'ビジュアル',
  offer: 'オファー',
  trust: '信頼',
  layout: 'レイアウト',
  other: 'その他',
};

export interface BannerAbImageSummary {
  id: number;
  url: string;
  thumbnail_url?: string | null;
  style_name?: string | null;
  prompt?: string;
}

export interface BannerAbTestComparison {
  id: number;
  image_a_id: number;
  image_b_id: number;
  image_a: BannerAbImageSummary | null;
  image_b: BannerAbImageSummary | null;
  persona_count: number;
  votes_done: number;
  votes_skipped: number;
  mean_a: number | null;
  mean_b: number | null;
  delta: number | null;
  se: number | null;
  p_b_better: number | null;
  win_rate_b: number | null;
  verdict: BannerAbVerdict | null;
  factors: Record<BannerAbFactor, { a: number; b: number }> | null;
  reasons_top: string[] | null;
  /** 詳細 API だけが返す。仮想顧客のタイプ別の内訳（人数の多い順）。ペルソナが凍結されていない古いテストは空 */
  segments?: BannerAbSegment[];
  credits_charged: number;
  status: BannerAbComparisonStatus;
  error_message?: string | null;
}

/** 比較1件の、仮想顧客のタイプ（アーキタイプ）ひとつぶんの内訳 */
export interface BannerAbSegment {
  key: string;
  label: string;
  n: number;
  mean_a: number;
  mean_b: number;
  /** そのタイプで B を選んだ割合（同点を除く）。全員同点なら null */
  preference_b: number | null;
}

export interface BannerAbRanking {
  image_id: number;
  wins: number;
  losses: number;
  avg_delta: number | null;
  avg_p_better: number | null;
  /** 参加した比較でその画像が選ばれた割合の平均（同点を除く）。画面の主指標 */
  avg_preference?: number | null;
}

/** 勝った側が選ばれた割合から「差のはっきり度」を言葉にする。判定保留・割合なしは null */
export const preferenceStrength = (
  verdict: BannerAbVerdict | null,
  winRateB: number | null | undefined,
): string | null => {
  if (winRateB == null || (verdict !== 'a' && verdict !== 'b')) return null;
  const share = verdict === 'b' ? winRateB : 1 - winRateB;
  if (share >= 0.8) return 'はっきり差あり';
  if (share >= 0.6) return 'やや差あり';
  return '僅差';
};

/**
 * 1ラウンドぶんの改善仮説。HypothesisGenerator が作り、
 * BannerAbChallengerGenerator が投げたジョブの id を足して round.hypotheses に記録する。
 */
export interface BannerAbHypothesis {
  kind: 'edit' | 'appeal_swap';
  instruction: string;
  /** appeal_swap のときだけ入る訴求軸のキー。edit は null */
  appeal_axis: string | null;
  /** ImageGenerationJob の主キーは UUID 文字列。投入に失敗した仮説には付かない */
  job_id?: string;
}

/**
 * ループの 1 ラウンド（`banner_ab_test_rounds`）。詳細 API が `rounds` として返す。
 * pairwise にも round 0 が 1 行だけあり、そちらは challenger 系が null のまま completed になる。
 */
export interface BannerAbTestRound {
  id: number;
  round_no: number;
  champion_image_id: number | null;
  challenger_image_ids: number[] | null;
  /** 文字列（UUID）の配列。数値に変換しない */
  generation_job_ids: string[] | null;
  hypotheses: BannerAbHypothesis[] | null;
  status: BannerAbRoundStatus;
  winner_image_id: number | null;
  improved: boolean;
  /** そのラウンドの派生画像ぶんを credits_consumed に計上した額。null = 未計上 */
  image_credits?: number | null;
  started_at: string | null;
  completed_at: string | null;
}

export interface BannerAbTestResult {
  mode: BannerAbTestMode;
  ranking: BannerAbRanking[];
  winner_image_id: number | null;
  total_credits: number;
  disclaimer: string;
  /** 以下は auto_loop の stopLoop() だけが書く。王者の系譜と停止理由 */
  initial_champion?: number;
  final_champion?: number;
  /** 回り切ったラウンド数（`current_round`）。BannerAbTest.rounds の配列とは別物 */
  rounds?: number;
  /** 初代王者から交代のあったラウンドの勝者まで、重複を除いた並び */
  lineage?: number[];
  stopped_reason?: BannerAbStoppedReason;
}

export interface BannerAbTest {
  id: number;
  mode: BannerAbTestMode;
  status: BannerAbTestStatus;
  progress: number;
  progress_step: string | null;
  error_message: string | null;
  persona_count: number;
  candidate_image_ids: number[];
  winner_image_id: number | null;
  credits_estimated: number;
  credits_consumed: number;
  result: BannerAbTestResult | null;
  stopped_reason: string | null;
  /** auto_loop の現王者。pairwise は null */
  champion_image_id?: number | null;
  /** 進行中のラウンド番号。ループ未開始は 0、pairwise は常に 0 */
  current_round?: number;
  /** 設定した最大ラウンド。pairwise は 0 */
  max_rounds?: number;
  /** 詳細 API だけが返す。round_no の昇順 */
  rounds?: BannerAbTestRound[];
  comparisons?: BannerAbTestComparison[];
  created_at: string;
  completed_at: string | null;
  /** 一覧 API だけが返す。candidate_image_ids の順で、参照できない画像は落ちている */
  candidates?: BannerAbImageSummary[];
  /** 一覧 API だけが返す。winner_image_id の画像（無ければ null） */
  winner?: BannerAbImageSummary | null;
  /** 一覧 API だけが返す。候補画像に紐づく商品（候補順・重複除去） */
  products?: { id: number; name: string }[];
  /** 一覧 API だけが返す。勝者が選ばれた理由（最大6件） */
  winner_reasons?: string[];
  /** 一覧 API だけが返す。勝者の avg_p_better（0〜1）。集計前・勝者なしは null */
  winner_p?: number | null;
  /** 一覧 API だけが返す。勝者が参加した比較で勝者が選ばれた割合の平均（0〜1）。勝者なしは null */
  winner_preference?: number | null;
  /** 一覧 API だけが返す。勝者側の票が最も多かった因子。因子が付かなかったら null */
  winner_factor?: BannerAbFactor | null;
}

export interface BannerAbTestEstimate {
  votes: number;
  pool: number;
  total: number;
  comparisons: number;
  persona_count: number;
  needs_pool: boolean;
  mode?: BannerAbTestMode;
  /** 以下は auto_loop の estimateLoop() だけが返す（いずれも全ラウンドの合計） */
  images?: number;
  hypothesis?: number;
  rounds?: number;
  /** 1ラウンドの派生枚数。フロントで定数を持たずこの値を使う（backend の challengers_per_round が正） */
  challengers?: number;
}

export interface Paginated<T> {
  data: T[];
  current_page: number;
  last_page: number;
  total: number;
}
